import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Card, Form, Button, InputGroup, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCodeBranch, faMapMarkerAlt, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL, END_POINT_BRANCHES } from "../const";
import { useNavigate, Link } from "react-router-dom";
import { showSuccessToast } from "../utils/toast";
import CompanyCheckboxPicker from '../components/CompanyCheckboxPicker';

export const BranchForm = ({ onSuccess, onCancel, onSubmittingChange }) => {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
  const [companies, setCompanies] = useState([]);
  const [allowMultipleCompanies, setAllowMultipleCompanies] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companyLoadError, setCompanyLoadError] = useState("");
  const [companyReloadKey, setCompanyReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoadingCompanies(true);
    setCompanyLoadError("");
    axios.get(`${API_URL}auth/context`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (!active) return;
      if (!data.status || !Array.isArray(data.data?.companies)) {
        throw new Error('Invalid company information');
      }
      setCompanies(data.data.companies);
      setAllowMultipleCompanies(Boolean(data.data.is_super_admin || data.data.allow_multiple_companies));
    }).catch(() => {
      if (active) setCompanyLoadError('Unable to load companies. Please try again.');
    }).finally(() => {
      if (active) setLoadingCompanies(false);
    });
    return () => { active = false; };
  }, [token, companyReloadKey]);

  const formik = useFormik({
    initialValues: { name: "", address: "", company_ids: [] },
    validationSchema: Yup.object({
      name: Yup.string().required("Branch name is required"),
      address: Yup.string().required("Branch address is required"),
      company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      if (loadingCompanies || companyLoadError) {
        setSubmitting(false);
        return;
      }
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.post(API_URL + END_POINT_BRANCHES, values, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to create branch.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to create branch.');
      } finally {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  const fieldsDisabled = formik.isSubmitting || loadingCompanies || Boolean(companyLoadError);

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting || loadingCompanies}>
      {loadingCompanies && (
        <div className="d-flex align-items-center gap-2 text-muted mb-3" role="status">
          <Spinner animation="border" size="sm" />
          <span>Loading companies...</span>
        </div>
      )}
      {companyLoadError && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faExclamationCircle} />
            {companyLoadError}
          </span>
          <Button variant="link" className="p-0" type="button" onClick={() => setCompanyReloadKey(value => value + 1)}>
            Retry
          </Button>
        </Alert>
      )}
      {formik.status && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{formik.status}</span>
        </Alert>
      )}
      <fieldset disabled={fieldsDisabled}>
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="branch-name">
              <Form.Label>Branch Name <span className="text-danger">*</span></Form.Label>
              <InputGroup className="has-validation">
                <InputGroup.Text><FontAwesomeIcon icon={faCodeBranch} /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="e.g. Downtown Branch"
                  {...formik.getFieldProps("name")}
                  isInvalid={formik.touched.name && formik.errors.name}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="branch-address">
              <Form.Label>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted me-1" />
                Branch Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Street, city, state, postal code"
                {...formik.getFieldProps("address")}
                isInvalid={formik.touched.address && formik.errors.address}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.address}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          {(allowMultipleCompanies || companies.length > 1) && (
            <Col sm={12} className="mb-3">
              <CompanyCheckboxPicker
                companies={companies}
                selectedIds={formik.values.company_ids}
                onChange={(companyIds) => formik.setFieldValue('company_ids', companyIds)}
                error={formik.touched.company_ids && formik.errors.company_ids}
              />
            </Col>
          )}
        </Row>
      </fieldset>
      <hr className="my-3" />
      <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          className="btn-quote-blue py-2 px-3 box-shedow-none outline-none d-inline-flex align-items-center gap-2"
          type="submit"
          disabled={fieldsDisabled}
        >
          {formik.isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" />}
          {formik.isSubmitting ? 'Saving...' : 'Save Branch'}
        </Button>
      </div>
    </Form>
  );
};

export const CreateBranchForm = () => {
  const navigate = useNavigate();

  const handleSuccess = async (result) => {
    showSuccessToast(result.message)
    navigate('/branches');
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Add Branch</h5>
          <Link to="/branches" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
            Back to Listing
          </Link>
        </div>
        <BranchForm onSuccess={handleSuccess} />
      </Card.Body>
    </Card>
  );
};
